import React from 'react';
import { motion } from 'framer-motion';
import { blogPosts, categories } from '../blogData'; // Import data

const Blog = () => {
  // Categories for filter (now imported)
  // const categories = [ ... ]; // Removed

  // Sample blog posts (now imported)
  // const blogPosts = [ ... ]; // Removed

  return (
    <div className="blog-page pt-24 pb-16">
      <div className="section-container">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-6">Drone Insights</h1>
          <p className="text-xl text-lightText/80 max-w-3xl mx-auto">
            Explore our latest articles, guides, and insights about drone photography, 
            videography, and the industry at large, with a special focus on Lehigh Valley, Pennsylvania.
          </p>
        </motion.div>

        {/* Featured Post - Displaying the first post from the array */}
        {blogPosts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div className="relative aspect-[21/9] overflow-hidden rounded-xl mb-8">
            <img 
              src={blogPosts[0].image} 
              alt={blogPosts[0].title} 
              className="w-full h-full object-cover"
            />
              <div className="absolute inset-0 bg-gradient-to-t from-darkBg/80 via-darkBg/40 to-transparent flex flex-col justify-end p-8">
              <span className="text-primary font-medium mb-2">{blogPosts[0].category}</span>
                <h2 className="text-2xl md:text-4xl font-bold text-lightText mb-4">{blogPosts[0].title}</h2>
                <p className="text-lg text-lightText/80 max-w-3xl mb-6">{blogPosts[0].excerpt}</p>
              <div className="flex items-center gap-4">
                <img 
                  src={blogPosts[0].authorImage} 
                  alt={blogPosts[0].authorName} 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                    <p className="text-lightText font-medium">{blogPosts[0].authorName}</p>
                    <p className="text-lightText/60 text-sm">{blogPosts[0].date}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <a 
                href={`/blog/${blogPosts[0].slug}`} 
                className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md transition-colors inline-block"
            >
              Read Full Article
            </a>
          </div>
        </motion.div>
        )}

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-5 py-2 rounded-full transition-colors ${
                  index === 0 // Assuming 'All' is always the first category and active by default
                    ? 'bg-primary text-darkBg'
                    : 'bg-darkCard text-lightText/70 hover:bg-darkCard/70'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Grid - Displaying the rest of the posts (from the second post onwards) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.slice(1).map((post, index) => (
            <BlogCard key={post.id} post={post} index={index} />
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-16">
          <nav className="flex items-center gap-2">
            <button className="w-10 h-10 flex items-center justify-center rounded-md bg-darkCard text-lightText/70 hover:bg-darkCard/70 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button className="w-10 h-10 flex items-center justify-center rounded-md bg-primary text-darkBg">1</button>
            <button className="w-10 h-10 flex items-center justify-center rounded-md bg-darkCard text-lightText/70 hover:bg-darkCard/70 transition-colors">2</button>
            <button className="w-10 h-10 flex items-center justify-center rounded-md bg-darkCard text-lightText/70 hover:bg-darkCard/70 transition-colors">3</button>
            <button className="w-10 h-10 flex items-center justify-center rounded-md bg-darkCard text-lightText/70 hover:bg-darkCard/70 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Blog Card Component
const BlogCard = ({ post, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 + (index * 0.1) }}
      className="glass-card rounded-lg overflow-hidden h-full flex flex-col"
    >
      <div className="aspect-video overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title} 
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex justify-between items-center mb-4">
          <span className="text-primary text-sm font-medium">{post.category}</span>
          <span className="text-lightText/60 text-sm">{post.date}</span>
        </div>
        <h3 className="text-xl font-bold text-lightText mb-3">{post.title}</h3>
        <p className="text-lightText/80 mb-6 flex-grow">{post.excerpt}</p>
        <div className="flex justify-between items-center mt-auto pt-4 border-t border-darkCard/50">
          <div className="flex items-center">
            <img 
              src={post.authorImage} 
              alt={post.authorName} 
              className="w-8 h-8 rounded-full object-cover mr-3"
            />
            <span className="text-lightText text-sm">{post.authorName}</span>
          </div>
          <a 
            href={`/blog/${post.slug}`} 
            className="text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Read More →
          </a>
        </div>
      </div>
    </motion.article>
  );
};

export default Blog; 